import { Image, Title } from "components";
import { useLocation } from "react-router-dom";
import { isProRoute } from "subscriptions/helpers";
// @ts-expect-error
import { IntercomAPI } from "react-intercom";
import { Button, ButtonGroup, Spacer, Text, Flex, Alert, TextAnchor } from "components";

import {
  CheckoutPurposeEnumType,
  useCreateStripeCheckoutSessionMutation,
  useCreateStripePortalSessionMutation,
  useSubscriptionStatusQuery,
} from "generated/graphql";
import { useEffect, useState } from "react";
import { ProFeatures, useSubscriptionPlanFeatures } from "feShared/subscriptions/hooks/useSubscriptionPlanFeatures";
import { useBreakpoints, useGetBusinessUnitUserRole, useHasFeature } from "hooks";
import pro from "assets/img/Subscription/pro.png";
import { getBUFromLocalStorage } from "helpers/storage";
import styles from "./Paywall.module.scss";
import cn from "classnames";

export const Paywall = () => {
  const { isBreakpointS, isBreakpointL } = useBreakpoints();
  const businessUnitId = getBUFromLocalStorage();
  const { pathname } = useLocation();
  const { isProFeatureEnabledForBU } = useSubscriptionPlanFeatures(businessUnitId, isProRoute(pathname));
  const hasAnimalManagementSubscriptionFeature = isProFeatureEnabledForBU(ProFeatures.AnimalManagement);
  const { isAdmin } = useGetBusinessUnitUserRole();

  const [error, setError] = useState<boolean>(false);
  const [createCheckoutSession] = useCreateStripeCheckoutSessionMutation();
  const [createStripePortalSession] = useCreateStripePortalSessionMutation();

  const isPaywallFoggleActive = useHasFeature("SHOW_PAYWALL");

  const showPaywall = isPaywallFoggleActive && !hasAnimalManagementSubscriptionFeature && isProRoute(pathname);

  const { data: subscriptionStatusData } = useSubscriptionStatusQuery({
    variables: { businessUnitId },
    fetchPolicy: "cache-and-network",
  });

  const activationTokenId = subscriptionStatusData?.subscriptionStatus?.activationTokens[0]?.id || undefined;

  const handleOpenIntercom = (): void => {
    IntercomAPI("showNewMessage", "I have a question regarding my subscription?\n\n");
  };

  const handleUpgrade = async () => {
    try {
      if (activationTokenId) {
        const { data: checkoutSessionData } = await createCheckoutSession({
          variables: {
            input: {
              activationTokenId,
              businessUnitId,
              returnUrl: window.location.href,
              purpose: CheckoutPurposeEnumType.Activate,
            },
          },
        });

        if (checkoutSessionData && checkoutSessionData?.createStripeCheckoutSession && window.location.href) {
          document.location.href = checkoutSessionData?.createStripeCheckoutSession.sessionUrl;
        }
      } else if (subscriptionStatusData?.subscriptionStatus?.hasStripeSubscriptions) {
        const { data } = await createStripePortalSession({
          variables: {
            input: {
              businessUnitId,
              returnUrl: window.location.href,
            },
          },
        });

        if (data && data?.createStripePortalSession && document.location.href) {
          document.location.href = data?.createStripePortalSession.sessionUrl;
        }
      } else {
        const { data: checkoutSessionData } = await createCheckoutSession({
          variables: {
            input: {
              activationTokenId,
              businessUnitId,
              returnUrl: window.location.href,
              purpose: CheckoutPurposeEnumType.Pro,
            },
          },
        });

        if (checkoutSessionData && checkoutSessionData?.createStripeCheckoutSession) {
          document.location.href = checkoutSessionData?.createStripeCheckoutSession.sessionUrl;
        }
      }
    } catch (error: unknown) {
      setError(true);
    }
  };

  useEffect(() => {
    if (showPaywall) {
      // Ensure the content behind the paywall isn't scrollable.
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showPaywall]);

  if (!showPaywall) {
    return null;
  }

  return (
    <div className={styles.background}>
      <div
        className={cn(styles.modal, {
          [styles["modal--breakpoint_s"]]: isBreakpointS,
          [styles["modal--breakpoint_l"]]: isBreakpointL,
        })}
      >
        <Title secondary>Upgrade Required</Title>
        <Spacer baselineHeight={4} />
        <Flex container containerDirection="row" containerWrap={isBreakpointL ? "nowrap" : "wrap"}>
          <Flex item xs="fill" l={6} itemOrder={isBreakpointL ? 1 : 2}>
            {error && <Alert text="Unable to upgrade subscription. Please contact Breedr support." color="RED" />}

            <Text>
              Breedr Pro is priced to support British farmers and you can get all the benefits of Pro for FREE when you
              buy or sell 20 animals.
            </Text>
            <Spacer baselineHeight={1} />
            <Text>Included:</Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Multi-Species</strong> management for your sheep and cattle
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Quick Weighing</strong> weigh with ease with crush mode
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Complete Compliance</strong> manage movement and medicines
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Access Support</strong> 30 minute onboarding session and phone support
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Advanced Insights</strong> with our state of the art reporting tools
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Breeding</strong> maximise your herd performance in the app
            </Text>
            <Spacer baselineHeight={2} />
            <Text>
              Our individual animal analytics helps you discover key areas for improvement in DLWG, breeding &amp;
              health, and you can track your progress in our easy &amp; time-saving app.
            </Text>
            <Spacer baselineHeight={2} />
            <Text>12 months free when you trade livestock with Breedr or £29.50+VAT a month.</Text>
            <Spacer baselineHeight={2} />
            <TextAnchor target="_blank" caption="Find out more" href="https://www.breedr.co/features-and-pricing/" />

            <Spacer baselineHeight={2} />
            {isAdmin && (
              <ButtonGroup>
                <Button caption="Upgrade" onClick={handleUpgrade} disabled={error} />
                <Button variant="hollow" caption="Get in Contact" colour="grey" onClick={handleOpenIntercom} />
              </ButtonGroup>
            )}
          </Flex>
          <Flex item itemGutter xs="fill" l={6} itemOrder={isBreakpointL ? 2 : 1}>
            <Image src={pro} />
            {!isBreakpointL && <Spacer baselineHeight={2} />}
          </Flex>
        </Flex>
      </div>
    </div>
  );
};
